import React, { useEffect, useState } from 'react';
import { useAdmin } from '../../contexts/AdminContext';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const { admin, dashboardStats, loadDashboardStats, loading } = useAdmin();
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadDashboardStats();
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadDashboardStats();
    setRefreshing(false);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="admin-dashboard">
      <div className="dashboard-header">
        <div className="header-content">
          <h1>Admin Dashboard</h1>
          <p>Hoş geldiniz, {admin?.first_name} {admin?.last_name}</p>
        </div>
        <button 
          className="refresh-btn"
          onClick={handleRefresh}
          disabled={refreshing}
        >
          {refreshing ? '🔄' : '↻'} Yenile
        </button>
      </div>

      {dashboardStats ? (
        <div className="dashboard-content">
          {/* Driver Statistics */}
          <div className="stats-section">
            <h2>👥 Taxifahrer İstatistikleri</h2>
            <div className="stats-grid">
              <div className="stat-card primary">
                <div className="stat-icon">🚖</div>
                <div className="stat-content">
                  <h3>{dashboardStats.drivers.total_drivers}</h3>
                  <p>Toplam Taxifahrer</p>
                </div>
              </div>
              
              <div className="stat-card success">
                <div className="stat-icon">✅</div>
                <div className="stat-content">
                  <h3>{dashboardStats.drivers.active_drivers}</h3>
                  <p>Aktif Taxifahrer</p>
                </div>
              </div>
              
              <div className="stat-card online">
                <div className="stat-icon">🟢</div>
                <div className="stat-content">
                  <h3>{dashboardStats.online_drivers}</h3>
                  <p>Online Taxifahrer</p>
                </div>
              </div>
              
              <div className="stat-card warning">
                <div className="stat-icon">⚠️</div>
                <div className="stat-content">
                  <h3>{dashboardStats.drivers.inactive_drivers}</h3>
                  <p>İnaktif Taxifahrer</p>
                </div>
              </div>
            </div>
          </div>

          {/* Event Statistics */}
          <div className="stats-section">
            <h2>📅 Event İstatistikleri</h2>
            <div className="stats-grid">
              <div className="stat-card info">
                <div className="stat-icon">📋</div>
                <div className="stat-content">
                  <h3>{dashboardStats.events.total_events}</h3>
                  <p>Toplam Eventler</p>
                </div>
              </div>
              
              <div className="stat-card success">
                <div className="stat-icon">🎉</div>
                <div className="stat-content">
                  <h3>{dashboardStats.events.published_events}</h3>
                  <p>Yayınlanan Eventler</p>
                </div>
              </div>
              
              <div className="stat-card primary">
                <div className="stat-icon">⏰</div>
                <div className="stat-content">
                  <h3>{dashboardStats.events.upcoming_events}</h3>
                  <p>Yaklaşan Eventler</p>
                </div>
              </div>
            </div>
          </div>

          {/* City Distribution */}
          <div className="stats-section">
            <h2>🌍 Şehir Dağılımı</h2>
            <div className="city-distribution">
              {dashboardStats.city_distribution.map((city, index) => (
                <div key={index} className="city-item">
                  <div className="city-name">{city.city}</div>
                  <div className="city-count">{city.driver_count} taxifahrer</div>
                  <div className="city-bar">
                    <div 
                      className="city-bar-fill"
                      style={{
                        width: `${(city.driver_count / dashboardStats.drivers.total_drivers) * 100}%`
                      }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Activity */}
          <div className="stats-section">
            <h2>📈 Son Aktiviteler</h2>
            <div className="recent-activity">
              {dashboardStats.recent_activity.length > 0 ? (
                dashboardStats.recent_activity.map((activity, index) => (
                  <div key={index} className="activity-item">
                    <div className="activity-icon">
                      {activity.action === 'LOGIN' && '🔑'}
                      {activity.action === 'CREATE_DRIVER' && '👤➕'}
                      {activity.action === 'UPDATE_DRIVER' && '👤✏️'}
                      {activity.action === 'DELETE_DRIVER' && '👤❌'}
                      {activity.action === 'CREATE_EVENT' && '📅➕'}
                      {activity.action === 'UPDATE_EVENT' && '📅✏️'}
                      {activity.action === 'DELETE_EVENT' && '📅❌'}
                    </div>
                    <div className="activity-content">
                      <div className="activity-action">
                        {activity.action === 'LOGIN' && 'Admin Girişi'}
                        {activity.action === 'CREATE_DRIVER' && 'Yeni Taxifahrer Eklendi'}
                        {activity.action === 'UPDATE_DRIVER' && 'Taxifahrer Güncellendi'}
                        {activity.action === 'DELETE_DRIVER' && 'Taxifahrer Silindi'}
                        {activity.action === 'CREATE_EVENT' && 'Yeni Event Eklendi'}
                        {activity.action === 'UPDATE_EVENT' && 'Event Güncellendi'}
                        {activity.action === 'DELETE_EVENT' && 'Event Silindi'}
                      </div>
                      <div className="activity-time">
                        {formatDate(activity.created_at)}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-activity">
                  <span className="no-activity-icon">📝</span>
                  <p>Henüz aktivite bulunmuyor</p>
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="stats-section">
            <h2>⚡ Hızlı İşlemler</h2>
            <div className="quick-actions">
              <a href="/admin/drivers/new" className="quick-action-btn primary">
                <span className="action-icon">👤➕</span>
                <span>Yeni Taxifahrer Ekle</span>
              </a>
              <a href="/admin/events/new" className="quick-action-btn success">
                <span className="action-icon">📅➕</span>
                <span>Yeni Event Ekle</span>
              </a>
              <a href="/admin/drivers" className="quick-action-btn info">
                <span className="action-icon">👥</span>
                <span>Tüm Taxifahrerleri Görüntüle</span>
              </a>
              <a href="/admin/events" className="quick-action-btn warning">
                <span className="action-icon">📋</span>
                <span>Tüm Eventleri Görüntüle</span>
              </a>
            </div>
          </div>
        </div>
      ) : (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Dashboard yükleniyor...</p>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
